// What a project can filter on and put in the text: one read per project, shared by By
// details, Reach a level's targets and the chips under the message.

import { useCallback, useEffect, useState } from 'react';
import type { SearchableOption } from '../SearchableSelect';
import { bannerAPI, type BannerDetailField, type BannerDetailOptions, type DetailRef } from './bannerApi';

export const detailKey = (f: DetailRef): string => `${f.scope}|${f.source}|${f.key}`;

/** A store's "City" reads "Store City" on a control, where its group is out of sight. */
export const detailLabel = (f: { scope: string; label: string }): string =>
  f.scope === 'store' && !/^store\b/i.test(f.label) ? `Store ${f.label}` : f.label;

export function useBannerDetails(open: boolean, projectId: number | null) {
  const [options, setOptions] = useState<(BannerDetailOptions & { projectId: number }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open || projectId == null || options?.projectId === projectId) return;
    let cancelled = false;
    setError(null);
    bannerAPI
      .detailOptions(projectId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        setOptions({ fields: data?.fields ?? [], tokens: data?.tokens ?? [], projectId });
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Could not load this project's details.");
      });
    return () => {
      cancelled = true;
    };
    // Only a new project or a retry reads them again.
  }, [open, projectId, options?.projectId, attempt]);

  const current = options?.projectId === projectId ? options : null;
  const fields = current?.fields ?? [];
  // A date or a tenure has no list of values to pick from.
  const pickable = fields.filter((f) => f.values.length > 0);
  const asOptions = (list: BannerDetailField[]): SearchableOption[] =>
    list.map((f) => ({
      value: detailKey(f),
      label: detailLabel(f),
      group: f.scope === 'store' ? 'Store details' : 'Employee details',
    }));

  return {
    loaded: current != null,
    error,
    retry: useCallback(() => setAttempt((n) => n + 1), []),
    fields,
    tokens: current?.tokens ?? [],
    fieldOptions: asOptions(pickable),
    // A date sets no target: the server refuses one.
    targetFieldOptions: asOptions(pickable.filter((f) => f.kind !== 'date')),
    fieldOf: (key: string) => pickable.find((f) => detailKey(f) === key) ?? null,
  };
}

export type BannerDetails = ReturnType<typeof useBannerDetails>;
